import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { AIProvider } from "@/context/AIContext";
import { CompassProvider } from "@/context/CompassContext";
import AppShell from "@/components/AppShell";
import ScrollToTop from "@/components/ScrollToTop";
import SmartEntry from "@/components/SmartEntry";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Hoy from "@/pages/Hoy";
import MiNegocio from "@/pages/MiNegocio";
import Construir from "@/pages/Construir";
import ExplorarRutas from "@/pages/ExplorarRutas";
import PlaybookDetail from "@/pages/PlaybookDetail";
import Clientes from "@/pages/Clientes";
import IA from "@/pages/IA";
import Configuracion from "@/pages/Configuracion";
import MisRutas from "@/pages/MisRutas";
import CompassHome from "@/pages/compass/CompassHome";
import CompassCountry from "@/pages/compass/CompassCountry";
import CompassCity from "@/pages/compass/CompassCity";
import CompassIdea from "@/pages/compass/CompassIdea";
import CompassNegocio from "@/pages/compass/CompassNegocio";
import CompassAnalyzing from "@/pages/compass/CompassAnalyzing";
import CompassResult from "@/pages/compass/CompassResult";
import "@/App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
        <div className="label-mono">Cargando…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <AppShell>{children}</AppShell>;
}

// Compass flow lives OUTSIDE AppShell so it can be fully mobile-first
// (no sidebar / bottom nav) while still requiring auth.
function ProtectedRaw({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
        <div className="label-mono">Cargando…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AIProvider>
          <CompassProvider>
            <BrowserRouter>
              <ScrollToTop />
              <SmartEntry>
                <Toaster position="bottom-right" richColors={false} />
                <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/registro" element={<Register />} />

                {/* Compass flow (mobile-first, no shell) */}
                <Route path="/brujula" element={<ProtectedRaw><CompassHome /></ProtectedRaw>} />
                <Route path="/brujula/pais" element={<ProtectedRaw><CompassCountry /></ProtectedRaw>} />
                <Route path="/brujula/ciudad" element={<ProtectedRaw><CompassCity /></ProtectedRaw>} />
                <Route path="/brujula/idea" element={<ProtectedRaw><CompassIdea /></ProtectedRaw>} />
                <Route path="/brujula/negocio" element={<ProtectedRaw><CompassNegocio /></ProtectedRaw>} />
                <Route path="/brujula/analizando" element={<ProtectedRaw><CompassAnalyzing /></ProtectedRaw>} />
                <Route path="/brujula/resultado/:id" element={<ProtectedRaw><CompassResult /></ProtectedRaw>} />

                {/* Existing app */}
                <Route path="/" element={<Protected><Hoy /></Protected>} />
                <Route path="/mis-rutas" element={<Protected><MisRutas /></Protected>} />
                <Route path="/negocio" element={<Protected><MiNegocio /></Protected>} />
                <Route path="/construir" element={<Protected><Construir /></Protected>} />
                <Route path="/explorar" element={<Protected><ExplorarRutas /></Protected>} />
                {/* Old /playbooks entry route → redirect to Mis Rutas (spec: never mix templates with active work). */}
                <Route path="/playbooks" element={<Navigate to="/mis-rutas" replace />} />
                <Route path="/playbooks/:id" element={<Protected><PlaybookDetail /></Protected>} />
                <Route path="/clientes" element={<Protected><Clientes /></Protected>} />
                <Route path="/ruta" element={<Protected><IA /></Protected>} />
                <Route path="/ia" element={<Navigate to="/ruta" replace />} />
                <Route path="/configuracion" element={<Protected><Configuracion /></Protected>} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </SmartEntry>
            </BrowserRouter>
          </CompassProvider>
        </AIProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
